import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardHeader,
  CardBody,
  Icon,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { WarningTwoIcon, CheckCircleIcon } from '@chakra-ui/icons';

const carveOutData = [
  {
    id: 'CLM-9921-A',
    patient: 'Maria Garcia',
    dos: '2026-07-08',
    billedCode: 'D7140',
    description: 'Extraction, erupted tooth or exposed root',
    originalPlan: 'PPS (T1015)',
    status: 'Flagged',
    carveOutRule: 'Medi-Cal Dental Manual: Certain oral surgery services are carved out of the FQHC PPS rate and must be billed Fee-For-Service (FFS) directly to Medi-Cal Dental.',
    ffsRate: '$65.00',
    action: 'Re-route to FFS'
  },
  {
    id: 'CLM-8832-B',
    patient: 'James Wilson',
    dos: '2026-07-09',
    billedCode: 'H0020',
    description: 'Methadone administration and/or service',
    originalPlan: 'PPS (T1015)',
    status: 'Flagged',
    carveOutRule: 'Medi-Cal Provider Manual (SUD): Narcotic Treatment Program (NTP) services, including methadone administration, are carved out and reimbursed at the FFS rate.',
    ffsRate: '$18.52',
    action: 'Re-route to FFS'
  }
];

const FqDemoCarveOutAnalyzer = () => {
  return (
    <Container maxW="container.xl" py={8}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="lg" color="brand.700" mb={2}>Medi-Cal Carve-Out Analyzer</Heading>
          <Text color="gray.600">
            Detecting services that must be billed Fee-For-Service (FFS) outside the standard PPS rate, using exact DHCS fee schedules.
          </Text>
        </Box>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Intelligence Layer Active</AlertTitle>
            <AlertDescription display="block">
              Revedy has cross-referenced incoming claims against the Medi-Cal Provider Manuals and the current DHCS Fee Schedule (rates_data_clean.csv).
            </AlertDescription>
          </Box>
        </Alert>

        <Card variant="outline" bg="white">
          <CardHeader bg="gray.50" borderBottomWidth="1px">
            <HStack justify="space-between">
              <Heading size="md">Flagged Carve-Out Opportunities</Heading>
              <Badge colorScheme="orange" p={2} borderRadius="md">2 Claims Require Action</Badge>
            </HStack>
          </CardHeader>
          <CardBody p={0}>
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Claim ID / Patient</Th>
                  <Th>Service Code</Th>
                  <Th>Original Plan</Th>
                  <Th>Revedy Intelligence</Th>
                  <Th isNumeric>Exact FFS Rate</Th>
                  <Th>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {carveOutData.map((claim, idx) => (
                  <Tr key={idx}>
                    <Td>
                      <Text fontWeight="bold">{claim.id}</Text>
                      <Text fontSize="sm" color="gray.500">{claim.patient}</Text>
                      <Text fontSize="xs" color="gray.400">DOS: {claim.dos}</Text>
                    </Td>
                    <Td>
                      <Badge colorScheme="purple" mb={1}>{claim.billedCode}</Badge>
                      <Text fontSize="xs" noOfLines={2} maxW="150px">{claim.description}</Text>
                    </Td>
                    <Td>
                      <Badge colorScheme="gray">{claim.originalPlan}</Badge>
                    </Td>
                    <Td maxW="300px">
                      <HStack align="start">
                        <Icon as={WarningTwoIcon} color="orange.500" mt={1} />
                        <Text fontSize="sm" color="gray.700">
                          {claim.carveOutRule}
                        </Text>
                      </HStack>
                    </Td>
                    <Td isNumeric>
                      <Text fontWeight="bold" color="green.600">{claim.ffsRate}</Text>
                      <Text fontSize="xs" color="gray.500">DHCS Schedule</Text>
                    </Td>
                    <Td>
                      <Button size="sm" colorScheme="blue" leftIcon={<CheckCircleIcon />}>
                        {claim.action}
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
};

export default FqDemoCarveOutAnalyzer;
